/**
 * @file Implements a DisjointIntervalSet using an augmented Red-Black Tree.
 */

/**
 * Represents a half-open interval [start, end).
 */
export interface IInterval {
    /**
     * The start of the interval (inclusive).
     */
    start: number;
    /**
     * The end of the interval (exclusive).
     */
    end: number;
}

enum Color {
    RED,
    BLACK
}

class Node {
    constructor(interval: IInterval, color: Color, parent: Node, left: Node, right: Node) {
        this.interval = interval;
        this.color = color;
        this.parent = parent;
        this.left = left;
        this.right = right;
        this.maxEnd = interval.end;
    }

    color: Color;
    interval: IInterval;
    parent: Node;
    left: Node;
    right: Node;

    /**
     * The maximum end value of all intervals in the subtree rooted at this node.
     */
    maxEnd: number;
}

/**
 * Maintains a collection of numeric intervals that are always kept in a disjoint, sorted state.
 *
 * @remarks
 * ### Core Concepts
 * All intervals managed by this class adhere to the **half-open interval** convention,
 * denoted `[start, end)`, where the `start` is inclusive and the `end` is exclusive.
 *
 * The class ensures that all intervals in its collection are disjoint. Contiguous
 * intervals (e.g., `[0, 5)` and `[5, 10)`) are automatically merged into a single
 * interval (`[0, 10)`) upon insertion.
 *
 * ### Implementation
 * This set is implemented using an augmented Red-Black Tree, where each node stores
 * an interval and is augmented with the maximum end value of all intervals in its subtree.
 * This allows for efficient querying.
 *
 * - **Insert Performance:** O(m * log n), where `n` is the number of intervals and `m` is the number of intervals merged.
 * - **Remove Performance:** O(k * log n), where `n` is the number of intervals and `k` is the number of intervals affected.
 * - **Query Performance:** O(k + log n), where `k` is the number of reported intervals.
 */
export class DisjointIntervalSet {
    private readonly nil: Node;
    private root: Node;

    private findMergeable(node: Node, interval: IInterval, result: IInterval[]): void {
        if (node === this.nil) {
            return;
        }
        // An interval [a, b) is mergeable with [s, e) if a <= e and b >= s.
        const isMergeable = node.interval.start <= interval.end && node.interval.end >= interval.start;

        if (isMergeable) {
            result.push(node.interval);
        }

        if (node.left !== this.nil && node.left.maxEnd >= interval.start) {
            this.findMergeable(node.left, interval, result);
        }
        if (node.right !== this.nil && node.interval.start <= interval.end) {
            this.findMergeable(node.right, interval, result);
        }
    }

    private searchOverlapping(node: Node, range: IInterval, result: IInterval[]): void {
        if (node === this.nil) {
            return;
        }
        // Overlap condition: a < y AND b > x for interval [a,b) and range [x,y)
        const overlaps = node.interval.start < range.end && node.interval.end > range.start;
        if (overlaps) {
            result.push(node.interval);
        }

        if (node.left !== this.nil && node.left.maxEnd > range.start) {
            this.searchOverlapping(node.left, range, result);
        }
        if (node.right !== this.nil && node.interval.start < range.end) {
            this.searchOverlapping(node.right, range, result);
        }
    }

    private inOrderTraversal(node: Node, result: IInterval[]): void {
        if (node !== this.nil) {
            this.inOrderTraversal(node.left, result);
            result.push(node.interval);
            this.inOrderTraversal(node.right, result);
        }
    }

    private findNodeByStart(start: number): Node | null {
        let current = this.root;
        while (current !== this.nil) {
            if (start === current.interval.start) {
                return current;
            }
            current = start < current.interval.start ? current.left : current.right;
        }
        return null;
    }

    private updateMaxEnd(node: Node): void {
        if (node !== this.nil) {
            node.maxEnd = Math.max(node.interval.end, node.left.maxEnd, node.right.maxEnd);
        }
    }

    private propagateMaxEndUpdate(node: Node): void {
        let current = node;
        while (current !== this.nil) {
            this.updateMaxEnd(current);
            current = current.parent;
        }
    }

    private leftRotate(x: Node): void {
        const y = x.right;
        x.right = y.left;
        if (y.left !== this.nil) {
            y.left.parent = x;
        }
        y.parent = x.parent;
        if (x.parent === this.nil) {
            this.root = y;
        } else if (x === x.parent.left) {
            x.parent.left = y;
        } else {
            x.parent.right = y;
        }
        y.left = x;
        x.parent = y;
        this.updateMaxEnd(x);
        this.updateMaxEnd(y);
    }

    private rightRotate(y: Node): void {
        const x = y.left;
        y.left = x.right;
        if (x.right !== this.nil) {
            x.right.parent = y;
        }
        x.parent = y.parent;
        if (y.parent === this.nil) {
            this.root = x;
        } else if (y === y.parent.right) {
            y.parent.right = x;
        } else {
            y.parent.left = x;
        }
        x.right = y;
        y.parent = x;
        this.updateMaxEnd(y);
        this.updateMaxEnd(x);
    }

    private rbInsert(z: Node): void {
        let y = this.nil;
        let x = this.root;
        while (x !== this.nil) {
            y = x;
            if (z.interval.start < x.interval.start) {
                x = x.left;
            } else {
                x = x.right;
            }
        }
        z.parent = y;
        if (y === this.nil) {
            this.root = z;
        } else if (z.interval.start < y.interval.start) {
            y.left = z;
        } else {
            y.right = z;
        }
        z.left = this.nil;
        z.right = this.nil;
        z.color = Color.RED;
        this.insertFixup(z);
    }

    private insertFixup(z: Node): void {
        let currentNode = z;
        while (currentNode.parent.color === Color.RED) {
            if (currentNode.parent === currentNode.parent.parent.left) {
                const y = currentNode.parent.parent.right;
                if (y.color === Color.RED) {
                    currentNode.parent.color = Color.BLACK;
                    y.color = Color.BLACK;
                    currentNode.parent.parent.color = Color.RED;
                    currentNode = currentNode.parent.parent;
                } else {
                    if (currentNode === currentNode.parent.right) {
                        currentNode = currentNode.parent;
                        this.leftRotate(currentNode);
                    }
                    currentNode.parent.color = Color.BLACK;
                    currentNode.parent.parent.color = Color.RED;
                    this.rightRotate(currentNode.parent.parent);
                }
            } else {
                const y = currentNode.parent.parent.left;
                if (y.color === Color.RED) {
                    currentNode.parent.color = Color.BLACK;
                    y.color = Color.BLACK;
                    currentNode.parent.parent.color = Color.RED;
                    currentNode = currentNode.parent.parent;
                } else {
                    if (currentNode === currentNode.parent.left) {
                        currentNode = currentNode.parent;
                        this.rightRotate(currentNode);
                    }
                    currentNode.parent.color = Color.BLACK;
                    currentNode.parent.parent.color = Color.RED;
                    this.leftRotate(currentNode.parent.parent);
                }
            }
        }
        this.root.color = Color.BLACK;
        this.propagateMaxEndUpdate(currentNode);
    }

    private transplant(u: Node, v: Node): void {
        if (u.parent === this.nil) {
            this.root = v;
        } else if (u === u.parent.left) {
            u.parent.left = v;
        } else {
            u.parent.right = v;
        }
        v.parent = u.parent;
    }

    private minimum(node: Node): Node {
        let current = node;
        while (current.left !== this.nil) {
            current = current.left;
        }
        return current;
    }

    private deleteNode(z: Node): void {
        let y = z;
        let yOriginalColor = y.color;
        let x: Node;
        let fixupStartNodeParent: Node;

        if (z.left === this.nil) {
            x = z.right;
            fixupStartNodeParent = z.parent;
            this.transplant(z, z.right);
        } else if (z.right === this.nil) {
            x = z.left;
            fixupStartNodeParent = z.parent;
            this.transplant(z, z.left);
        } else {
            y = this.minimum(z.right);
            yOriginalColor = y.color;
            x = y.right;
            if (y.parent === z) {
                x.parent = y;
                fixupStartNodeParent = y;
            } else {
                fixupStartNodeParent = y.parent;
                this.transplant(y, y.right);
                y.right = z.right;
                y.right.parent = y;
            }
            this.transplant(z, y);
            y.left = z.left;
            y.left.parent = y;
            y.color = z.color;
        }

        if (yOriginalColor === Color.BLACK) {
            this.deleteFixup(x);
        }

        this.propagateMaxEndUpdate(fixupStartNodeParent);
    }

    private deleteFixup(x: Node): void {
        let currentNode = x;
        while (currentNode !== this.root && currentNode.color === Color.BLACK) {
            if (currentNode === currentNode.parent.left) {
                let w = currentNode.parent.right;
                if (w.color === Color.RED) {
                    w.color = Color.BLACK;
                    currentNode.parent.color = Color.RED;
                    this.leftRotate(currentNode.parent);
                    w = currentNode.parent.right;
                }
                if (w.left.color === Color.BLACK && w.right.color === Color.BLACK) {
                    w.color = Color.RED;
                    currentNode = currentNode.parent;
                } else {
                    if (w.right.color === Color.BLACK) {
                        w.left.color = Color.BLACK;
                        w.color = Color.RED;
                        this.rightRotate(w);
                        w = currentNode.parent.right;
                    }
                    w.color = currentNode.parent.color;
                    currentNode.parent.color = Color.BLACK;
                    w.right.color = Color.BLACK;
                    this.leftRotate(currentNode.parent);
                    currentNode = this.root;
                }
            } else {
                let w = currentNode.parent.left;
                if (w.color === Color.RED) {
                    w.color = Color.BLACK;
                    currentNode.parent.color = Color.RED;
                    this.rightRotate(currentNode.parent);
                    w = currentNode.parent.left;
                }
                if (w.right.color === Color.BLACK && w.left.color === Color.BLACK) {
                    w.color = Color.RED;
                    currentNode = currentNode.parent;
                } else {
                    if (w.left.color === Color.BLACK) {
                        w.right.color = Color.BLACK;
                        w.color = Color.RED;
                        this.leftRotate(w);
                        w = currentNode.parent.left;
                    }
                    w.color = currentNode.parent.color;
                    currentNode.parent.color = Color.BLACK;
                    w.left.color = Color.BLACK;
                    this.rightRotate(currentNode.parent);
                    currentNode = this.root;
                }
            }
        }
        currentNode.color = Color.BLACK;
    }

    constructor() {
        // The nil node is a sentinel that represents a leaf node. It is created with placeholder parent/children, which are then updated to point to itself.
        this.nil = new Node({ start: -1, end: -1 }, Color.BLACK, {} as Node, {} as Node, {} as Node);
        this.nil.parent = this.nil;
        this.nil.left = this.nil;
        this.nil.right = this.nil;
        this.nil.maxEnd = -Infinity;
        this.root = this.nil;
    }

    /**
     * Adds a new interval to the set.
     *
     * The method automatically merges the new interval with any existing intervals that
     * are overlapping or contiguous, ensuring the disjoint property is maintained.
     *
     * @remarks
     * - **Time Complexity:** O(m * log n), where `n` is the total number of intervals
     * and `m` is the number of intervals being merged.
     *
     * @param interval The interval to insert. Must satisfy `start < end`.
     *
     * @example
     * // Case 1: Insertion into a gap
     * // set = {[10, 20)}
     * // insert([0, 5)) => set = {[0, 5), [10, 20)}
     *
     * @example
     * // Case 2: Merging/extending
     * // set = {[0, 5), [10, 20)}
     * // insert([4, 11)) => set = {[0, 20)}
     */
    insert(interval: IInterval): void {
        if (interval.start >= interval.end) {
            return; // Invalid interval
        }

        let mergedInterval = { ...interval };
        const toMerge: IInterval[] = [];
        this.findMergeable(this.root, interval, toMerge);

        for (const i of toMerge) {
            mergedInterval.start = Math.min(mergedInterval.start, i.start);
            mergedInterval.end = Math.max(mergedInterval.end, i.end);
        }

        // Batch delete the intervals that have been merged.
        for (const i of toMerge) {
            const nodeToDelete = this.findNodeByStart(i.start);
            if (nodeToDelete) {
                this.deleteNode(nodeToDelete);
            }
        }

        const newNode = new Node(mergedInterval, Color.RED, this.nil, this.nil, this.nil);
        this.rbInsert(newNode);
    }

    /**
     * Removes a range of values from the set.
     *
     * This operation may result in the truncation, full deletion, or splitting of
     * existing intervals. If the removal range does not overlap with any existing
     * interval, the set remains unchanged.
     *
     * @remarks
     * - **Time Complexity:** O(k * log n), where `n` is the total number of
     * intervals in the set and `k` is the number of intervals that overlap
     * with the removal range.
     *
     * @param interval The range to remove. Must satisfy `start < end`.
     *
     * @example
     * // Truncation: Removing [0, 5) from [0, 20) results in [5, 20).
     * // Splitting: Removing [5, 10) from [0, 20) results in [0, 5) and [10, 20).
     */
    remove(interval: IInterval): void {
        if (interval.start >= interval.end) {
            return;
        }

        const overlappingIntervals = this.query(interval);
        const toAdd: IInterval[] = [];

        for (const oldInterval of overlappingIntervals) {
            // Calculate remaining pieces and stage them for insertion.
            if (oldInterval.start < interval.start) {
                toAdd.push({ start: oldInterval.start, end: interval.start });
            }
            if (oldInterval.end > interval.end) {
                toAdd.push({ start: interval.end, end: oldInterval.end });
            }
        }

        // Batch delete all overlapping intervals.
        for (const oldInterval of overlappingIntervals) {
            const nodeToDelete = this.findNodeByStart(oldInterval.start);
            if (nodeToDelete) {
                this.deleteNode(nodeToDelete);
            }
        }

        // Batch insert the new pieces.
        for (const i of toAdd) {
            this.insert(i);
        }
    }

    /**
     * Searches the set and returns a sorted array of all stored intervals that
     * overlap with the given query range.
     *
     * @remarks
     * - An overlap between a stored interval `[a, b)` and the query range `[x, y)`
     * occurs if `a < y` and `b > x`.
     * - **Time Complexity:** O(k + log n), where `n` is the total number of intervals
     * in the set and `k` is the number of intervals in the result.
     *
     * @param range The range to query for overlapping intervals.
     * @returns A sorted array of overlapping intervals.
     */
    query(range: IInterval): IInterval[] {
        if (range.start >= range.end) {
            return [];
        }
        const result: IInterval[] = [];
        this.searchOverlapping(this.root, range, result);
        return result.sort((a, b) => a.start - b.start);
    }

    /**
     * Searches the set for intervals that overlap with the given query range,
     * and returns the intersection of those intervals with the query range.
     *
     * @remarks
     * This method effectively "trims" the stored intervals to the boundaries of
     * the query range.
     * - **Time Complexity:** O(k + log n), where `n` is the total number of intervals
     * in the set and `k` is the number of intervals that overlap with the query range.
     *
     * @param range The range to query for.
     * @returns A sorted array of intervals representing the parts of stored
     *          intervals that fall strictly within the query range.
     */
    queryAndTrim(range: IInterval): IInterval[] {
        const overlapping = this.query(range);
        if (overlapping.length === 0) {
            return [];
        }

        const result: IInterval[] = [];

        // Handle the first interval, which may be partially trimmed.
        const first = overlapping[0];
        const firstStart = Math.max(first.start, range.start);
        const firstEnd = Math.min(first.end, range.end);
        if (firstStart < firstEnd) {
            result.push({ start: firstStart, end: firstEnd });
        }

        // All middle intervals are guaranteed to be fully contained.
        // We can add them directly without trimming calculations.
        for (let i = 1; i < overlapping.length - 1; i++) {
            result.push(overlapping[i]);
        }

        // Handle the last interval, which may also be partially trimmed.
        if (overlapping.length > 1) {
            const last = overlapping[overlapping.length - 1];
            const lastStart = Math.max(last.start, range.start);
            const lastEnd = Math.min(last.end, range.end);
            if (lastStart < lastEnd) {
                result.push({ start: lastStart, end: lastEnd });
            }
        }

        return result;
    }

    /**
     * Returns a sorted array of all intervals currently in the set.
     *
     * @remarks
     * - **Time Complexity:** O(n), where `n` is the total number of intervals
     * in the set, as this requires a full traversal.
     *
     * @returns A sorted array of all intervals.
     */
    getIntervals(): IInterval[] {
        const result: IInterval[] = [];
        this.inOrderTraversal(this.root, result);
        return result;
    }
}
